import React, { useState, useRef, useEffect } from 'react';
import { Target, Eye, Heart, Lightbulb, Users, Award, Globe, Clock } from 'lucide-react';
import { aboutData } from '../data/mock';

export const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredValue, setHoveredValue] = useState(null);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const valueIcons = {
    "Innovation First": Lightbulb,
    "Customer Success": Users,
    "Reliability": Award,
    "Transparency": Globe
  };

  return (
    <section id="about" ref={sectionRef} className="py-20 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-40 left-10 w-48 h-48 rounded-full opacity-5 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-primary), transparent)`,
          animationDelay: '1s'
        }}></div>
        <div className="absolute bottom-20 right-20 w-36 h-36 rounded-full opacity-10 animate-float" style={{
          background: `radial-gradient(circle, var(--accent-purple), transparent)`,
          animationDelay: '3s'
        }}></div>
        
        {/* Decorative Lines */}
        <div className="absolute top-1/3 left-0 right-0 h-px opacity-20" style={{
          background: `linear-gradient(90deg, transparent, var(--accent-primary), transparent)`
        }}></div>
        <div className="absolute bottom-1/3 left-0 right-0 h-px opacity-20" style={{
          background: `linear-gradient(90deg, transparent, var(--accent-purple), transparent)`
        }}></div>
      </div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-16 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="inline-flex items-center space-x-2 px-4 py-2 rounded-full mb-6" style={{
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-primary)'
          }}>
            <Heart className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
            <span className="body-sm font-medium" style={{ color: 'var(--accent-primary)' }}>
              About Us
            </span>
          </div>
          <h2 className="display-md mb-6" style={{ color: 'var(--text-primary)' }}>
            Pioneering the Future of Automation
          </h2>
          <p className="body-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
            Founded with a vision to eliminate manual work and unlock human potential, 
            Zany Automation has become a trusted partner for businesses worldwide.
          </p>
        </div>

        {/* Mission & Vision */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
          {/* Mission */}
          <div className={`transition-all duration-1000 delay-200 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
          }`}>
            <div className="p-8 rounded-2xl h-full" style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)'
            }}>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center animate-pulse-glow" style={{
                  background: 'var(--accent-primary)'
                }}>
                  <Target className="w-8 h-8" style={{ color: 'var(--bg-primary)' }} />
                </div>
                <h3 className="h2" style={{ color: 'var(--text-primary)' }}>
                  Our Mission
                </h3>
              </div>
              <p className="body-lg" style={{ color: 'var(--text-secondary)' }}>
                {aboutData.mission}
              </p>
            </div>
          </div>

          {/* Vision */}
          <div className={`transition-all duration-1000 delay-400 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            <div className="p-8 rounded-2xl h-full" style={{
              background: 'var(--bg-secondary)',
              border: '1px solid var(--border-subtle)'
            }}>
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 rounded-xl flex items-center justify-center animate-pulse-glow" style={{
                  background: 'var(--accent-purple)'
                }}>
                  <Eye className="w-8 h-8" style={{ color: 'var(--text-primary)' }} />
                </div>
                <h3 className="h2" style={{ color: 'var(--text-primary)' }}>
                  Our Vision
                </h3>
              </div>
              <p className="body-lg" style={{ color: 'var(--text-secondary)' }}>
                {aboutData.vision}
              </p>
            </div>
          </div>
        </div>

        {/* Core Values */}
        <div className={`mb-20 transition-all duration-1000 delay-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="text-center mb-12">
            <h3 className="h1 mb-4" style={{ color: 'var(--text-primary)' }}>
              Our Core Values
            </h3>
            <p className="body-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
              The principles that guide everything we do and shape our company culture
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutData.values.map((value, index) => {
              const IconComponent = valueIcons[value.title];
              const isHovered = hoveredValue === index;
              
              return (
                <div
                  key={index}
                  className={`group p-6 rounded-2xl transition-all duration-300 cursor-pointer ${
                    isHovered ? 'scale-105' : ''
                  }`}
                  style={{
                    background: 'var(--bg-secondary)',
                    border: `1px solid ${isHovered ? 'var(--accent-primary)' : 'var(--border-subtle)'}`
                  }}
                  onMouseEnter={() => setHoveredValue(index)}
                  onMouseLeave={() => setHoveredValue(null)}
                >
                  {/* Icon */}
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-all duration-300 ${
                    isHovered ? 'animate-pulse-glow' : ''
                  }`} style={{
                    background: isHovered ? 'var(--accent-primary)' : 'var(--bg-tertiary)'
                  }}>
                    <IconComponent 
                      className="w-6 h-6" 
                      style={{ 
                        color: isHovered ? 'var(--bg-primary)' : 'var(--accent-primary)' 
                      }} 
                    />
                  </div>

                  {/* Title */}
                  <h4 className="h4 mb-3 group-hover:text-white transition-colors duration-300" 
                      style={{ color: 'var(--text-primary)' }}>
                    {value.title}
                  </h4>

                  {/* Description */}
                  <p className="body-sm group-hover:text-gray-200 transition-colors duration-300" 
                     style={{ color: 'var(--text-secondary)' }}>
                    {value.description}
                  </p>

                  {/* Bottom Accent */}
                  <div className={`w-full h-1 rounded-full mt-4 transition-all duration-300 ${
                    isHovered ? 'opacity-100' : 'opacity-0'
                  }`} style={{
                    background: 'var(--accent-primary)'
                  }}></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Company Stats */}
        <div className={`transition-all duration-1000 delay-800 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="text-center mb-12">
            <h3 className="h1 mb-4" style={{ color: 'var(--text-primary)' }}>
              Our Journey in Numbers
            </h3>
            <p className="body-lg max-w-2xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
              Key milestones and achievements that define our growth and impact
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {aboutData.stats.map((stat, index) => (
              <div
                key={index}
                className="group text-center p-6 rounded-2xl transition-all duration-300 hover:scale-105"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                {/* Animated Number */}
                <div className="relative mb-3">
                  <div className="h1 group-hover:scale-110 transition-transform duration-300" 
                       style={{ color: 'var(--accent-primary)' }}>
                    {stat.number}
                  </div>
                  
                  {/* Floating Icon */}
                  <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center" style={{
                      background: 'var(--accent-primary)'
                    }}>
                      <Clock className="w-3 h-3" style={{ color: 'var(--bg-primary)' }} />
                    </div>
                  </div>
                </div>
                
                <div className="body-md group-hover:text-white transition-colors duration-300" 
                     style={{ color: 'var(--text-muted)' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <div className={`text-center mt-20 transition-all duration-1000 delay-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
        }`}>
          <div className="max-w-3xl mx-auto p-8 rounded-2xl relative overflow-hidden" style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border-subtle)'
          }}>
            {/* Background Animation */}
            <div className="absolute inset-0 opacity-5">
              <div className="absolute top-0 left-0 w-20 h-20 rounded-full animate-float" style={{
                background: `radial-gradient(circle, var(--accent-primary), transparent)`,
                animationDelay: '1s'
              }}></div>
              <div className="absolute bottom-0 right-0 w-16 h-16 rounded-full animate-float" style={{
                background: `radial-gradient(circle, var(--accent-purple), transparent)`,
                animationDelay: '2s'
              }}></div>
            </div>

            <div className="relative z-10">
              <h3 className="h2 mb-4" style={{ color: 'var(--text-primary)' }}>
                Ready to Transform Your Business?
              </h3>
              <p className="body-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                Join hundreds of companies that have already revolutionized their operations with our automation solutions.
              </p>
              <button 
                className="btn-primary animate-pulse-glow"
                onClick={() => {
                  const element = document.querySelector('#contact');
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
              >
                Start Your Journey Today
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};